import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { T, Var } from 'gt-react';
import { useEffect, useRef, useState } from 'react';
import z from 'zod';

import useAuth from 'src/api/auth/useAuth';
import { useWorkOSCallback } from 'src/api/auth/useWorkOSCallback';
import config from 'src/config';
import { Route as rootRoute } from 'src/pages/__root';

import { MIN_AUTH_LOADING_MS } from './auth-constants';
import { AuthErrorMessage } from './AuthErrorMessage';
import { AuthShell } from './AuthShell';
import { consumePostLoginRedirect } from './postLoginRedirect';
import { Spinner } from 'src/components/ui/spinner';

export const Route = createRoute({
  path: '/auth/wos/callback',
  getParentRoute: () => rootRoute,
  validateSearch: z.object({
    code: z.string().optional(),
    state: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.currentUser && !search.code) {
      throw redirect({ to: '/' });
    }
  },
  component: WorkOSCallbackPage,
});

export function WorkOSCallbackPage() {
  const navigate = useNavigate();
  const { refetchUser } = useAuth();
  const { code, state, error: urlError, error_description } = Route.useSearch();
  const [hasProcessed, setHasProcessed] = useState(false);
  const [minLoadingActive, setMinLoadingActive] = useState(false);
  const callbackStartRef = useRef(0);

  const {
    mutate: workOSCallbackApi,
    isLoading: isAuthenticating,
    error: callbackError,
  } = useWorkOSCallback(async () => {
    const remaining =
      callbackStartRef.current + MIN_AUTH_LOADING_MS - Date.now();
    if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
    await refetchUser();
    navigate({ href: consumePostLoginRedirect() });
  });

  useEffect(() => {
    if (urlError) {
      return;
    }

    if (code && !hasProcessed) {
      setHasProcessed(true);
      setMinLoadingActive(true);
      callbackStartRef.current = Date.now();
      setTimeout(() => setMinLoadingActive(false), MIN_AUTH_LOADING_MS);
      workOSCallbackApi({ code, state });
    } else if (!code && !hasProcessed) {
      setHasProcessed(true);
    }
  }, [code, hasProcessed, state, urlError, workOSCallbackApi]);

  const displayError = urlError
    ? new Error(error_description || urlError)
    : callbackError;
  const isProcessingAuthentication =
    (!hasProcessed && !urlError) || isAuthenticating || minLoadingActive;
  const shouldShowError = Boolean(displayError) || (!code && hasProcessed);

  return (
    <AuthShell
      washOut={isProcessingAuthentication}
      title={
        isProcessingAuthentication ? (
          <T context="heading shown while SSO authentication is being completed on the callback page">
            Logging in to <Var>{config.appName}</Var>
          </T>
        ) : (
          <T context="heading shown when SSO authentication fails on the callback page">
            We couldn&apos;t complete your sign-in
          </T>
        )
      }
      description={
        isProcessingAuthentication ? (
          <T context="description shown while SSO authentication is being completed on the callback page">
            Finalizing your secure session...
          </T>
        ) : (
          <T context="description shown when SSO authentication fails on the callback page">
            The authentication response didn&apos;t complete cleanly. Review the
            message below and try again.
          </T>
        )
      }
    >
      <div className="py-2">
        {isProcessingAuthentication ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <Spinner size="xl" />
            <p className="text-center text-gray-500 text-sm font-light leading-relaxed">
              <T context="status message shown while SSO authentication is being completed on the callback page">
                Confirming your identity provider response and preparing your
                workspace.
              </T>
            </p>
          </div>
        ) : shouldShowError ? (
          <div className="space-y-4">
            <p className="text-center text-gray-500 text-sm font-light leading-relaxed">
              {!code && !displayError ? (
                <T context="error message when no authorization code was received during SSO callback">
                  Authentication failed because no authorization code was
                  returned.
                </T>
              ) : (
                <T context="generic authentication failure message on the SSO callback page">
                  Authentication failed. Please try again.
                </T>
              )}
            </p>
            <AuthErrorMessage error={displayError} />
          </div>
        ) : null}
      </div>
    </AuthShell>
  );
}
